import type File from '../../interfaces/File';

/** Default modal height (`95vh`). Max allows full cover (`100vh`). */
export const VIEWER_MODAL_DEFAULT_SIZE_VH = 95;
export const VIEWER_MODAL_MIN_SIZE_VH = 40;
export const VIEWER_MODAL_MAX_SIZE_VH = 100;
/** Approximate modal header chrome for body `calc` height. */
export const VIEWER_MODAL_HEADER_OFFSET_PX = 55;
/** Ant Design Modal default z-index. */
export const VIEWER_MODAL_Z_INDEX = 1000;
/** @deprecated Nested panel replaced stacking; kept for any stale imports. */
export const RIGHT_PANEL_ABOVE_VIEWER_MODAL_Z_INDEX = 1100;
/** Nested Details/AI split width inside FilegrantViewerModal (matches global RightPanel). */
export const RIGHT_PANEL_WIDTH_PX = 480;
/** @deprecated No longer used — modal stays full width with nested panel. */
export const VIEWER_MODAL_MIN_WIDTH_PX = 320;

export function clampViewerModalSizeVh(vh: number): number {
  if (!Number.isFinite(vh)) return VIEWER_MODAL_DEFAULT_SIZE_VH;
  return Math.min(VIEWER_MODAL_MAX_SIZE_VH, Math.max(VIEWER_MODAL_MIN_SIZE_VH, Math.round(vh)));
}

export type ViewerPreviewMetaParams = {
  fileId: number | string;
  /** Snapshot for RightPanel Details/Chat when list selection empty (filename open). */
  file?: File | null;
  version?: string | null;
  publicFileToken?: string | null;
  acToken?: string | null;
  isReadOnly?: boolean;
};

/**
 * Preview modal metadata only. Open state lives in the URL (`/files/.../preview/:fileId`);
 * fields apply only while `viewerModalFileId` matches the preview id in the path.
 */
export interface ViewerModalSlice {
  viewerModalFileId: string | null;
  viewerModalVersion: string | null;
  viewerModalPublicFileToken: string | null;
  viewerModalAcToken: string | null;
  viewerModalIsReadOnly: boolean;
  /** File meta for RightPanel sync while preview open (selection may be empty). */
  viewerModalFile: File | null;
  /** User-managed modal height in vh; persisted separately from open session. */
  viewerModalSizeVh: number;
  /** Stash metadata before navigating to the preview route. */
  setViewerPreviewMeta: (params: ViewerPreviewMetaParams) => void;
  setViewerModalFile: (file: File | null) => void;
  setViewerModalSizeVh: (vh: number) => void;
}

export const createViewerModalSlice = (set: any): ViewerModalSlice => ({
  viewerModalFileId: null,
  viewerModalVersion: null,
  viewerModalPublicFileToken: null,
  viewerModalAcToken: null,
  viewerModalIsReadOnly: false,
  viewerModalFile: null,
  viewerModalSizeVh: VIEWER_MODAL_DEFAULT_SIZE_VH,
  setViewerPreviewMeta: (params) =>
    set({
      viewerModalFileId: String(params.fileId),
      viewerModalVersion: params.version ?? null,
      viewerModalPublicFileToken: params.publicFileToken ?? null,
      viewerModalAcToken: params.acToken ?? null,
      viewerModalIsReadOnly: params.isReadOnly ?? false,
      viewerModalFile: params.file ?? null,
    }),
  setViewerModalFile: (file) => set({ viewerModalFile: file }),
  setViewerModalSizeVh: (vh) => set({ viewerModalSizeVh: clampViewerModalSizeVh(vh) }),
});
